import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MODULES } from "@/lib/modules";
import { Plus, X, Layers, Trash2, Users, Globe2, Building2, Edit3, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

// Catálogo de acciones soportadas (mirror del backend `module_actions.py`).
// El backend también lo expone vía /api/modules, pero lo dejamos local para evitar
// un round-trip extra al abrir el wizard.
const MODULE_ACTIONS = {
  M1:  [
    { id: "view",            label: "Ver usuarios" },
    { id: "create",          label: "Crear usuarios" },
    { id: "edit",            label: "Editar usuarios" },
    { id: "edit_commercial", label: "Editar términos comerciales" },
    { id: "deactivate",      label: "Desactivar usuarios" },
    { id: "delete",          label: "Eliminar usuarios" },
  ],
  M2:  [{ id: "view", label: "Ver diagnósticos" }, { id: "conduct", label: "Aplicar entrevista 1:1" }, { id: "manage_bank", label: "Editar banco de preguntas" }],
  M3:  [{ id: "view", label: "Ver diagnósticos" }, { id: "conduct", label: "Aplicar diagnóstico 1:1" }, { id: "manage_bank", label: "Editar banco de preguntas" }],
  M4:  [{ id: "view", label: "Ver planes" }, { id: "edit", label: "Editar plan" }, { id: "import_recommendations", label: "Importar recomendaciones" }],
  M5:  [{ id: "view", label: "Ver clases" }, { id: "schedule", label: "Agendar clases" }, { id: "join", label: "Entrar a sala" }, { id: "record", label: "Activar grabación" }],
  M6:  [{ id: "view", label: "Ver grabaciones" }, { id: "upload", label: "Subir pregrabadas" }, { id: "delete", label: "Eliminar grabaciones" }],
  M7:  [{ id: "view", label: "Ver catálogo" }, { id: "enroll", label: "Inscribirse" }, { id: "assign_courses", label: "Asignar cursos" }, { id: "certify_teacher", label: "Certificar docentes" }],
  M8:  [{ id: "view", label: "Ver perfiles" }, { id: "edit", label: "Editar perfiles" }],
  M9:  [{ id: "view", label: "Ver reportes" }, { id: "export", label: "Exportar" }, { id: "financial", label: "Reportes financieros" }],
  M10: [{ id: "view", label: "Ver auditoría" }, { id: "export", label: "Exportar logs" }],
  M11: [{ id: "view", label: "Ver mensajes" }, { id: "send", label: "Enviar mensajes" }],
  M12: [{ id: "view", label: "Ver pagos" }, { id: "billing", label: "Configurar facturación" }],
  M13: [{ id: "view", label: "Ver campañas" }, { id: "manage", label: "Editar campañas" }],
  M14: [
    { id: "view",       label: "Ver franquicias" },
    { id: "create",     label: "Crear franquicias" },
    { id: "edit",       label: "Editar franquicias" },
    { id: "delete",     label: "Eliminar franquicias" },
    { id: "commercial", label: "Editar términos comerciales" },
  ],
};

const allActionsOf = (id) => (MODULE_ACTIONS[id] || []).map((a) => a.id);

// Convierte el `modules` del perfil (legacy o granular) a forma canónica
// `[{ id, actions: [...] }]` y mantiene un orden estable.
function normalizeModules(modules) {
  if (!Array.isArray(modules)) return [];
  return modules.map((m) => {
    if (typeof m === "string") return { id: m, actions: allActionsOf(m) };
    const id = m.id;
    const valid = new Set(allActionsOf(id));
    const acts = (m.actions || []).filter((a) => valid.has(a));
    return { id, actions: acts.length ? acts : allActionsOf(id) };
  });
}

export default function AdminPerfilesPermisos() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";

  const [profiles, setProfiles] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/permission-profiles");
      setProfiles(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/franchises").then((r) => setFranchises(r.data || [])).catch(() => {});
  }, []);

  const onDelete = async (p) => {
    if (!confirm(`¿Eliminar el perfil "${p.name}"?`)) return;
    try {
      await api.delete(`/permission-profiles/${p.id}`);
      toast.success("Perfil eliminado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-perfiles-permisos-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#2C49A0]">Permisos modulares</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547]">Perfiles de acceso</h1>
          <p className="text-sm text-[#6E7591] mt-1">
            Define qué módulos y qué acciones específicas (crear, editar, eliminar, exportar…) puede ejecutar cada perfil.
          </p>
        </div>
        <button onClick={() => setEditing("new")} className="jasq-btn-primary inline-flex items-center gap-2" data-testid="open-create-profile">
          <Plus size={16} /> Crear perfil
        </button>
      </div>

      {loading ? (
        <div className="jasq-card p-10 text-center text-sm text-[#6E7591]">Cargando…</div>
      ) : profiles.length === 0 ? (
        <div className="jasq-card p-10 text-center">
          <Layers size={32} className="mx-auto text-[#2C49A0] mb-2" />
          <p className="font-bold text-[#1F2547]">Aún no hay perfiles</p>
          <p className="text-sm text-[#6E7591]">Crea el primero para empezar a asignar accesos por módulo y por acción.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {profiles.map((p) => (
            <ProfileCard
              key={p.id}
              profile={p}
              isOwner={isAdmin || (me?.role === "franquiciado" && p.scope === "franchise" && p.franchise_id === me?.franchise_id)}
              onEdit={() => setEditing(p)}
              onDelete={() => onDelete(p)}
            />
          ))}
        </div>
      )}

      {editing && (
        <ProfileWizard
          profile={editing === "new" ? null : editing}
          isAdmin={isAdmin}
          franchises={franchises}
          currentFranchiseId={me?.franchise_id || ""}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ProfileCard({ profile, isOwner, onEdit, onDelete }) {
  const ScopeIcon = profile.scope === "global" ? Globe2 : Building2;
  // v5.7.1 · el conteo coincide con las tarjetas visibles (módulos que la interfaz conoce)
  const norm = normalizeModules(profile.modules).filter((m) => MODULES.some((x) => x.id === m.id));
  const totalActions = norm.reduce((acc, m) => acc + m.actions.length, 0);

  return (
    <div className="jasq-card p-5" data-testid={`profile-${profile.id}`}>
      <div className="flex items-start gap-3">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${profile.color || "#F5B82E"}1A`, color: profile.color || "#F5B82E" }}>
          <Layers size={20} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="font-display font-extrabold text-[#1F2547] leading-tight">{profile.name}</p>
            <span className="jasq-pill bg-[#F2F4FA] text-[#2C49A0] shrink-0"><ScopeIcon size={12} />{profile.scope === "global" ? "Global" : profile.franchise_id || "—"}</span>
          </div>
          {profile.description && <p className="text-sm text-[#6E7591] mt-1">{profile.description}</p>}
        </div>
      </div>
      <div className="mt-4">
        <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#6E7591] mb-2">
          {norm.length} módulo{norm.length === 1 ? "" : "s"} · {totalActions} acción{totalActions === 1 ? "" : "es"}
        </p>
        <ModuleBadges modules={norm} />
      </div>
      <div className="mt-4 pt-3 border-t border-[#E1E5F0] flex items-center gap-3">
        <span className="jasq-pill bg-[#F2F4FA] text-[#1F2547]"><Users size={12} />{profile.users_count} usuario{profile.users_count === 1 ? "" : "s"}</span>
        <div className="flex-1" />
        {isOwner && (
          <>
            <button onClick={onEdit} className="jasq-btn-ghost inline-flex items-center gap-1.5 text-xs py-1.5 px-3" data-testid={`edit-profile-${profile.id}`}>
              <Edit3 size={13} /> Editar
            </button>
            <button onClick={onDelete} className="text-[#A52258] hover:underline text-xs font-bold inline-flex items-center gap-1" data-testid={`delete-profile-${profile.id}`}>
              <Trash2 size={13} /> Eliminar
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function ModuleBadges({ modules, max = 20 }) {
  const shown = modules.slice(0, max);
  return (
    <div className="flex flex-wrap gap-1.5">
      {shown.map((entry) => {
        const m = MODULES.find((x) => x.id === entry.id);
        if (!m) return null;
        const Icon = m.icon;
        const totalActs = (MODULE_ACTIONS[entry.id] || []).length;
        const has = entry.actions.length;
        return (
          <span key={entry.id} className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-bold border-2" style={{ borderColor: `${m.color}33`, color: m.color, background: `${m.color}0E` }}>
            <Icon size={12} />
            <span>{m.id}</span>
            <span className="text-[#1F2547] font-bold opacity-80">{m.short}</span>
            {totalActs > 0 ? (
              <span className="text-[10px] font-extrabold ml-1 px-1.5 py-0.5 rounded bg-white" style={{ color: m.color }}>{has}/{totalActs}</span>
            ) : (
              <span className="text-[10px] font-extrabold ml-1 px-1.5 py-0.5 rounded bg-white" style={{ color: m.color }}>Acceso</span>
            )}
          </span>
        );
      })}
      {modules.length > max && (
        <span className="inline-flex items-center px-2 py-1 text-[11px] font-bold rounded-lg bg-[#F2F4FA] text-[#1F2547]">+{modules.length - max}</span>
      )}
    </div>
  );
}

function ProfileWizard({ profile, isAdmin, franchises = [], currentFranchiseId = "", onClose, onSaved }) {
  const editing = !!profile;
  const initialModules = normalizeModules(profile?.modules || []);
  const [form, setForm] = useState({
    name: profile?.name || "",
    description: profile?.description || "",
    scope: profile?.scope || (isAdmin ? "global" : "franchise"),
    franchise_id: profile?.franchise_id || (isAdmin ? "" : currentFranchiseId),
    modules: initialModules,
    color: profile?.color || "#F5B82E",
  });
  const [expanded, setExpanded] = useState(() => new Set(initialModules.map((m) => m.id)));
  const [submitting, setSubmitting] = useState(false);

  const moduleEntry = (id) => form.modules.find((m) => m.id === id);

  const setModuleActions = (id, nextActions) => {
    setForm((f) => {
      const existing = f.modules.find((m) => m.id === id);
      if (nextActions.length === 0) {
        return { ...f, modules: f.modules.filter((m) => m.id !== id) };
      }
      if (!existing) return { ...f, modules: [...f.modules, { id, actions: nextActions }] };
      return { ...f, modules: f.modules.map((m) => (m.id === id ? { ...m, actions: nextActions } : m)) };
    });
  };

  const toggleAction = (mid, aid) => {
    const entry = moduleEntry(mid);
    const cur = entry?.actions || [];
    const next = cur.includes(aid) ? cur.filter((a) => a !== aid) : [...cur, aid];
    setModuleActions(mid, next);
  };

  const toggleAllForModule = (mid, on) => {
    setModuleActions(mid, on ? allActionsOf(mid) : []);
  };

  const toggleExpanded = (mid) => {
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(mid)) n.delete(mid); else n.add(mid);
      return n;
    });
  };

  const totalActions = form.modules.reduce((acc, m) => acc + m.actions.length, 0);
  const needsFranchise = !editing && isAdmin && form.scope === "franchise";
  const canSave = form.name.trim().length >= 2 && form.modules.length > 0 && (!needsFranchise || !!form.franchise_id);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        modules: form.modules,
        color: form.color,
      };
      if (editing) {
        await api.patch(`/permission-profiles/${profile.id}`, payload);
        toast.success("Perfil actualizado");
      } else {
        const createBody = { ...payload, scope: form.scope };
        if (form.scope === "franchise") createBody.franchise_id = form.franchise_id;
        await api.post("/permission-profiles", createBody);
        toast.success("Perfil creado");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setSubmitting(false); }
  };

  const COLORS = ["#F5B82E", "#D63384", "#2C49A0", "#3FA84D", "#1F2547"];

  return (
    <div className="fixed inset-0 z-50 bg-[#1F2547]/50 backdrop-blur-sm flex justify-end" data-testid="profile-wizard-modal">
      <div className="jasq-drawer-panel bg-white shadow-2xl w-full max-w-lg h-full overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1E5F0]">
          <div>
            <p className="text-[11px] tracking-wider uppercase font-extrabold text-[#2C49A0]">Perfil de permisos</p>
            <h3 className="font-display text-xl font-extrabold text-[#1F2547]">{editing ? "Editar perfil" : "Crear nuevo perfil"}</h3>
          </div>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]" data-testid="pwizard-close"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto min-h-0">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Nombre del perfil">
              <input data-testid="pwizard-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej. Docente Senior, Coordinador, Tutor"
                className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" />
            </Field>
            {isAdmin && !editing && (
              <Field label="Ámbito">
                <div className="flex gap-2">
                  {["global", "franchise"].map((s) => (
                    <button key={s} type="button" data-testid={`pwizard-scope-${s}`}
                      onClick={() => setForm({ ...form, scope: s })}
                      className={`flex-1 px-3 py-3 rounded-xl border-2 text-sm font-bold transition-all inline-flex items-center gap-2 justify-center ${
                        form.scope === s ? "border-[#1F2547] bg-[#1F2547] text-white" : "border-[#E1E5F0] hover:border-[#F5B82E]"
                      }`}
                    >
                      {s === "global" ? <Globe2 size={15} /> : <Building2 size={15} />}
                      {s === "global" ? "Global" : "Por franquicia"}
                    </button>
                  ))}
                </div>
              </Field>
            )}
          </div>

          {/* Selector de franquicia (solo cuando admin + scope=franchise) */}
          {needsFranchise && (
            <Field label="Franquicia">
              <select
                data-testid="pwizard-franchise"
                value={form.franchise_id}
                onChange={(e) => setForm({ ...form, franchise_id: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium bg-white"
              >
                <option value="">— Selecciona una franquicia —</option>
                {franchises
                  .filter((f) => (f.status || "active") === "active")
                  .map((f) => (
                    <option key={f.franchise_id} value={f.franchise_id}>
                      {f.is_headquarters ? "★ " : ""}{f.name}
                    </option>
                  ))}
              </select>
            </Field>
          )}

          <Field label="Descripción (opcional)">
            <input data-testid="pwizard-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Para qué casos usar este perfil"
              className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" />
          </Field>

          <Field label="Color identificador">
            <div className="flex gap-2">
              {COLORS.map((c) => (
                <button key={c} type="button"
                  onClick={() => setForm({ ...form, color: c })}
                  data-testid={`pwizard-color-${c}`}
                  aria-label={`color ${c}`}
                  className={`w-10 h-10 rounded-xl border-2 transition-all ${form.color === c ? "border-[#1F2547] scale-110" : "border-[#E1E5F0]"}`}
                  style={{ background: c }}
                />
              ))}
            </div>
          </Field>

          <div>
            <div className="flex items-end justify-between mb-2">
              <Field label={`Módulos y acciones (${form.modules.length} módulos · ${totalActions} acciones activas)`} />
              <div className="flex gap-2 text-[11px] font-bold">
                <button type="button" onClick={() => setForm({ ...form, modules: MODULES.filter((m) => !["MQA", "MREPO"].includes(m.id)).map((m) => ({ id: m.id, actions: allActionsOf(m.id) })) })}
                  className="text-[#2C49A0] hover:underline" data-testid="pwizard-select-all">Marcar todo</button>
                <span className="text-[#E1E5F0]">·</span>
                <button type="button" onClick={() => setForm({ ...form, modules: [] })}
                  className="text-[#A52258] hover:underline" data-testid="pwizard-clear">Limpiar</button>
              </div>
            </div>
            <div className="space-y-2">
              {MODULES.filter((m) => !["MQA", "MREPO"].includes(m.id)).map((m) => {
                const Icon = m.icon;
                const entry = moduleEntry(m.id);
                const acts = entry?.actions || [];
                const hasAny = acts.length > 0;
                const actionsCatalog = MODULE_ACTIONS[m.id] || [];
                const isExpanded = expanded.has(m.id);
                return (
                  <div key={m.id}
                    data-testid={`pwizard-mod-${m.id}`}
                    className={`rounded-xl border-2 transition-all overflow-hidden ${
                      hasAny ? "bg-[#FFF7DA]/40 border-[#F5B82E]" : "border-[#E1E5F0] bg-white"
                    }`}>
                    <div className="flex items-center gap-3 p-3">
                      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${m.color}1A`, color: m.color }}>
                        <Icon size={16} />
                      </span>
                      <button type="button" className="flex-1 min-w-0 text-left" onClick={() => toggleExpanded(m.id)} data-testid={`pwizard-mod-expand-${m.id}`}>
                        <div className="flex items-center gap-1.5">
                          {isExpanded ? <ChevronDown size={14} className="text-[#6E7591]" /> : <ChevronRight size={14} className="text-[#6E7591]" />}
                          <p className="font-bold text-sm text-[#1F2547] truncate">{m.id} · {m.name}</p>
                        </div>
                        <p className="text-[11px] text-[#6E7591] ml-5">
                          {hasAny ? `${acts.length} de ${actionsCatalog.length} acciones activas` : "Sin acceso a este módulo"}
                        </p>
                      </button>
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <span className="text-[11px] font-bold text-[#6E7591]">{hasAny ? "Activo" : "Inactivo"}</span>
                        <input
                          type="checkbox"
                          data-testid={`pwizard-mod-toggle-${m.id}`}
                          className="sr-only peer"
                          checked={hasAny}
                          onChange={(e) => toggleAllForModule(m.id, e.target.checked)}
                        />
                        <span className="w-10 h-6 rounded-full bg-[#E1E5F0] peer-checked:bg-[#F5B82E] relative transition-colors">
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${hasAny ? "translate-x-4" : ""}`} />
                        </span>
                      </label>
                    </div>
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-1 border-t border-[#F2F4FA] grid sm:grid-cols-2 gap-1.5">
                        {actionsCatalog.map((a) => {
                          const checked = acts.includes(a.id);
                          return (
                            <label key={a.id}
                              className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer text-xs font-bold transition-all ${
                                checked ? "bg-white border-2 border-[#F5B82E] text-[#1F2547]" : "bg-[#F7F8FA] border-2 border-transparent text-[#6E7591] hover:text-[#1F2547]"
                              }`}>
                              <input
                                type="checkbox"
                                data-testid={`pwizard-act-${m.id}-${a.id}`}
                                checked={checked}
                                onChange={() => toggleAction(m.id, a.id)}
                                className="accent-[#F5B82E]"
                              />
                              <span>{a.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#E1E5F0] bg-[#F7F8FA] flex items-center gap-3">
          <button onClick={onClose} className="jasq-btn-ghost">Cancelar</button>
          <div className="flex-1" />
          <button
            onClick={submit}
            disabled={!canSave || submitting}
            className="jasq-btn-primary"
            data-testid="pwizard-submit"
          >{submitting ? "Guardando…" : editing ? "Guardar cambios" : "Crear perfil"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591]">{label}</span>
      {children && <div className="mt-1.5">{children}</div>}
    </label>
  );
}
